'use client'

import { useState, useCallback, useContext, useRef, useEffect } from 'react'
import { TableEditContext } from '../../index'

type EditingCell = { rowId: string; colId: string } | null

interface CellEditConfig {
  singleClickEdit?: boolean
  confirmDelay?: number
  onNavigateNext?: (currentRowId: string, currentColId: string) => void
  onNavigateUp?: (currentRowId: string, currentColId: string) => void
  onNavigateDown?: (currentRowId: string, currentColId: string) => void
  onNavigateLeft?: (currentRowId: string, currentColId: string) => void
  onNavigateRight?: (currentRowId: string, currentColId: string) => void
}

export default function useInlineCellEdit(config: CellEditConfig = {}) {
  const {
    singleClickEdit = true,
    confirmDelay = 0,
    onNavigateNext,
    onNavigateUp,
    onNavigateDown,
    onNavigateLeft,
    onNavigateRight,
  } = config

  const ctx = useContext(TableEditContext) || {}
  const handleConfirmCellEdit = (ctx as { handleConfirmCellEdit?: (rowId: string, colId: string, newValue: string) => Promise<void> | void }).handleConfirmCellEdit

  const [editingCell, setEditingCell] = useState<EditingCell>(null)
  const [editingValue, setEditingValue] = useState<string>('')
  const editingValueRef = useRef<string>('')
  const isConfirmingRef = useRef<boolean>(false)
  const confirmTimerRef = useRef<NodeJS.Timeout | null>(null)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  const clearConfirmTimer = useCallback(() => {
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current)
      confirmTimerRef.current = null
    }
  }, [])

  useEffect(() => () => clearConfirmTimer(), [clearConfirmTimer])

  const isEditingCell = useCallback(
    (rowId: string, colId: string) => !!(editingCell && editingCell.rowId === rowId && editingCell.colId === colId),
    [editingCell]
  )

  const positionCursorAtEnd = useCallback((element: HTMLInputElement | HTMLTextAreaElement) => {
    requestAnimationFrame(() => {
      const length = element.value.length
      element.setSelectionRange(length, length)
      element.scrollTop = element.scrollHeight
    })
  }, [])

  const startEditing = useCallback((rowId: string, colId: string, initialValue: string | number = '') => {
    clearConfirmTimer()
    const safeInitialValue = initialValue === null || initialValue === undefined || initialValue === '' ? '' : String(initialValue)
    setEditingCell({ rowId, colId })
    setEditingValue(safeInitialValue)
    editingValueRef.current = safeInitialValue
  }, [clearConfirmTimer])

  const handleSingleClick = useCallback((rowId: string, colId: string, initialValue: string | number = '') => {
    if (singleClickEdit) startEditing(rowId, colId, initialValue)
  }, [singleClickEdit, startEditing])

  const handleDoubleClick = useCallback((rowId: string, colId: string, initialValue: string | number = '') => {
    if (!singleClickEdit) startEditing(rowId, colId, initialValue)
  }, [singleClickEdit, startEditing])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const newValue = e.target.value
    setEditingValue(newValue)
    editingValueRef.current = newValue
  }, [])

  const confirmEdit = useCallback(async () => {
    if (isConfirmingRef.current) return
    const cell = editingCell
    if (!cell || !handleConfirmCellEdit) return

    try {
      isConfirmingRef.current = true
      clearConfirmTimer()
      const value = String(editingValueRef.current ?? '').trim()
      await handleConfirmCellEdit(cell.rowId, cell.colId, value)
      setEditingCell(null)
      setEditingValue('')
      editingValueRef.current = ''
    } catch (error) {
      console.error('Error confirming edit:', error)
    } finally {
      isConfirmingRef.current = false
    }
  }, [editingCell, handleConfirmCellEdit, clearConfirmTimer])

  const cancelEdit = useCallback(() => {
    clearConfirmTimer()
    setEditingCell(null)
    setEditingValue('')
    editingValueRef.current = ''
  }, [clearConfirmTimer])

  const handleKeyDown = useCallback(async (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      await confirmEdit()
      if (onNavigateNext && editingCell) onNavigateNext(editingCell.rowId, editingCell.colId)
    } else if (e.key === 'Tab') {
      e.preventDefault()
      e.stopPropagation()
      await confirmEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      cancelEdit()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      e.stopPropagation()
      await confirmEdit()
      if (onNavigateUp && editingCell) onNavigateUp(editingCell.rowId, editingCell.colId)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      e.stopPropagation()
      await confirmEdit()
      if (onNavigateDown && editingCell) onNavigateDown(editingCell.rowId, editingCell.colId)
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      e.stopPropagation()
      await confirmEdit()
      if (onNavigateLeft && editingCell) onNavigateLeft(editingCell.rowId, editingCell.colId)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      e.stopPropagation()
      await confirmEdit()
      if (onNavigateRight && editingCell) onNavigateRight(editingCell.rowId, editingCell.colId)
    }
  }, [confirmEdit, cancelEdit, onNavigateNext, onNavigateUp, onNavigateDown, onNavigateLeft, onNavigateRight, editingCell])

  const handleBlur = useCallback(() => {
    clearConfirmTimer()
    if (confirmDelay > 0) {
      confirmTimerRef.current = setTimeout(() => confirmEdit(), confirmDelay)
    } else {
      confirmEdit()
    }
  }, [confirmDelay, confirmEdit, clearConfirmTimer])

  const setInputRef = useCallback((element: HTMLInputElement | HTMLTextAreaElement | null) => {
    inputRef.current = element
    if (element) {
      element.focus()
      positionCursorAtEnd(element)
    }
  }, [positionCursorAtEnd])

  return {
    editingCell,
    editingValue,
    isEditingCell,
    handleSingleClick,
    handleDoubleClick,
    handleChange,
    handleKeyDown,
    handleBlur,
    cancelEdit,
    setInputRef,
  }
}
